import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { PhoneInput } from '../ui/PhoneInput';
import { STRIPE_PROXY_URL } from '../../config/billing';
import { stripeFetch } from '../../api/stripe';

interface EditStripeCustomerModalProps {
  open: boolean;
  onClose: () => void;
  stripeCustomerId: string;
  onUpdated?: () => void;
}

export const EditStripeCustomerModal: React.FC<EditStripeCustomerModalProps> = ({
  open, onClose, stripeCustomerId, onUpdated,
}) => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    line1: '', line2: '', city: '', state: '', postal_code: '', country: 'US',
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !stripeCustomerId) return;
    setFetching(true);
    setError('');
    stripeFetch(`${STRIPE_PROXY_URL}/customers/${stripeCustomerId}`)
      .then(r => r.json())
      .then(c => {
        setForm({
          name: c.name || '',
          email: c.email || '',
          phone: c.phone || '',
          line1: c.address?.line1 || '',
          line2: c.address?.line2 || '',
          city: c.address?.city || '',
          state: c.address?.state || '',
          postal_code: c.address?.postal_code || '',
          country: c.address?.country || 'US',
        });
      })
      .catch(e => setError(e.message))
      .finally(() => setFetching(false));
  }, [open, stripeCustomerId]);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await stripeFetch(`${STRIPE_PROXY_URL}/customers/${stripeCustomerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          address: {
            line1: form.line1, line2: form.line2,
            city: form.city, state: form.state,
            postal_code: form.postal_code, country: form.country,
          },
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Failed'); }
      onUpdated?.();
      onClose();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [key]: e.target.value }));

  return (
    <Modal open={open} onClose={onClose} title="Edit Stripe Customer Info" size="lg">
      {fetching ? (
        <div className="text-center py-8 text-gray-500">Loading customer data...</div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Name" value={form.name} onChange={set('name')} />
            <Input label="Email" type="email" value={form.email} onChange={set('email')} />
            <PhoneInput label="Phone" value={form.phone} onChange={(v) => setForm(f => ({ ...f, phone: v }))} />
            <div />
            <Input label="Address Line 1" value={form.line1} onChange={set('line1')} />
            <Input label="Address Line 2" value={form.line2} onChange={set('line2')} />
            <Input label="City" value={form.city} onChange={set('city')} />
            <Input label="State" value={form.state} onChange={set('state')} />
            <Input label="ZIP / Postal Code" value={form.postal_code} onChange={set('postal_code')} />
            <Input label="Country" value={form.country} onChange={set('country')} />
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading}>{loading ? 'Saving...' : 'Save'}</Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
